import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = 'https://hoosierlifeinsurance.com';

export default function HowToChooseLifeInsuranceCompanyIndiana() {
  useEffect(() => {
    document.title = 'How to Choose a Life Insurance Company in Indiana — 2026 Guide';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'How to choose the best life insurance company in Indiana: financial strength ratings, underwriting, claims, and the 7 questions every Indiana family should ask before buying.');

    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'how to choose life insurance company Indiana, best life insurance companies Indiana, life insurance carrier ratings Indianapolis');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/resources/how-to-choose-life-insurance-company-indiana`;

    const schemas = [
      {
        '@context': 'https://schema.org', '@type': 'Article',
        headline: 'How to Choose a Life Insurance Company in Indiana',
        description: 'A complete guide to evaluating and selecting the right life insurance carrier for Indiana families — financial strength, underwriting, claims, and more.',
        author: { '@type': 'Person', name: 'Benjamin Wright' },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-01-18T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/resources/how-to-choose-life-insurance-company-indiana`,
      },
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Resources', item: `${SITE_URL}/resources` },
          { '@type': 'ListItem', position: 3, name: 'How to Choose a Life Insurance Company in Indiana', item: `${SITE_URL}/resources/how-to-choose-life-insurance-company-indiana` },
        ],
      },
    ];

    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-choose-${i}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    return () => { schemas.forEach((_, i) => document.getElementById(`schema-choose-${i}`)?.remove()); };
  }, []);

  const carriers = [
    { name: 'Northwestern Mutual', amBest: 'A++', moody: 'Aaa', sp: 'AA+', founded: 1857, strength: 'Highest financial strength; best for whole life and dividends', bestFor: 'Whole life, high-net-worth clients' },
    { name: 'MassMutual', amBest: 'A++', moody: 'Aa3', sp: 'AA+', founded: 1851, strength: 'Excellent dividends; strong whole life and disability products', bestFor: 'Whole life, disability income' },
    { name: 'New York Life', amBest: 'A++', moody: 'Aaa', sp: 'AA+', founded: 1845, strength: 'Oldest mutual insurer; exceptional financial stability', bestFor: 'Whole life, estate planning' },
    { name: 'Banner Life', amBest: 'A+', moody: 'A1', sp: 'AA-', founded: 1949, strength: 'Best term life rates; most lenient underwriting for many conditions', bestFor: 'Term life, smokers, health conditions' },
    { name: 'Protective Life', amBest: 'A+', moody: 'A1', sp: 'A+', founded: 1907, strength: 'Consistently lowest term rates; fast no-exam approval', bestFor: 'Term life, budget-conscious buyers' },
    { name: 'Mutual of Omaha', amBest: 'A+', moody: 'A1', sp: 'AA-', founded: 1909, strength: 'Best final expense and guaranteed issue products', bestFor: 'Final expense, seniors, guaranteed issue' },
  ];

  const criteria = [
    { icon: 'ri-award-line', title: 'Financial Strength Rating', weight: 'Critical', desc: 'A life insurance policy is a promise to pay decades from now. The company must be financially strong enough to keep that promise. Look for AM Best ratings of A or higher. A++ is the gold standard.', tip: 'Never buy from a carrier rated below A- by AM Best. The extra savings aren\'t worth the risk.' },
    { icon: 'ri-file-list-3-line', title: 'Underwriting Approach', weight: 'Very Important', desc: 'Different carriers underwrite health conditions very differently. One carrier might rate you "Standard" for diabetes while another offers "Preferred." Shopping multiple carriers is essential.', tip: 'An independent agent can match your health profile to the most lenient carrier — potentially saving you hundreds per year.' },
    { icon: 'ri-customer-service-2-line', title: 'Claims Payment History', weight: 'Very Important', desc: 'The whole point of life insurance is the death benefit. Check the carrier\'s claims payment ratio and average time to pay. Look for carriers that pay 99%+ of claims.', tip: 'Check NAIC complaint ratios. A ratio below 1.0 means fewer complaints than average for the industry.' },
    { icon: 'ri-price-tag-3-line', title: 'Premium Competitiveness', weight: 'Important', desc: 'For the same coverage and health class, rates can vary by 30-50% between carriers. Always compare at least 5 carriers before buying.', tip: 'The cheapest carrier isn\'t always the best — but you shouldn\'t pay significantly more than the market rate for the same coverage.' },
    { icon: 'ri-smartphone-line', title: 'Policy Management & Technology', weight: 'Moderate', desc: 'Can you manage your policy online? Is there a mobile app? Can you change beneficiaries digitally? Modern carriers offer full online policy management.', tip: 'This matters more for long-term policies (whole life) than short-term (term life).' },
    { icon: 'ri-user-settings-line', title: 'Agent & Service Quality', weight: 'Moderate', desc: 'How responsive is the carrier when you have questions? Do they have local Indiana agents? Is customer service available when you need it?', tip: 'Read Google reviews for the carrier\'s local Indiana office, not just the national company.' },
  ];

  return (
    <>
      <title>How to Choose a Life Insurance Company in Indiana — 2026 | Hoosier Life</title>
      <article className="min-h-screen bg-white">
        <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white py-20">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="mb-6">
              <Link to="/resources" className="inline-flex items-center text-white/70 hover:text-white transition-colors text-sm">
                <i className="ri-arrow-left-line mr-2" />Back to Resources
              </Link>
            </div>
            <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#D97642] text-sm font-semibold px-4 py-2 rounded-full mb-6">
              <i className="ri-building-2-line" />Carrier Selection
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">How to Choose a Life Insurance Company in Indiana — 7 Questions to Ask First</h1>
            <p className="text-xl text-white/80 mb-8">Not all life insurance companies are equal. Here's exactly what Indiana families should look for — and the 7 questions to ask before buying.</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span><i className="ri-calendar-line mr-2" />Updated January 2026</span>
              <span><i className="ri-time-line mr-2" />10 min read</span>
              <span><i className="ri-map-pin-line mr-2" />Indiana Carrier Guide</span>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Choosing a life insurance company is one of the most important financial decisions you'll make. You're trusting this company to pay your family — potentially decades from now — when they need it most.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This guide walks Indiana families through the 6 key criteria for evaluating carriers, a comparison of the top companies available in Indiana, and the 7 questions you should ask before signing anything.
            </p>
          </div>

          {/* 6 Criteria */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">6 Criteria for Evaluating Life Insurance Companies</h2>
            <div className="space-y-6">
              {criteria.map((c, i) => (
                <div key={c.title} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`${c.icon} text-2xl`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{c.title}</h3>
                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${c.weight === 'Critical' ? 'bg-red-100 text-red-700' : c.weight === 'Very Important' ? 'bg-orange-100 text-orange-700' : c.weight === 'Important' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{c.weight}</span>
                      </div>
                      <p className="text-gray-700 text-sm mb-4">{c.desc}</p>
                      <div className="bg-[#FDF6F0] rounded-lg p-4">
                        <p className="text-sm text-gray-800"><strong className="text-[#D97642]">Pro Tip:</strong> {c.tip}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Carrier Comparison */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Top Life Insurance Companies Available in Indiana</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-5 py-4 text-left font-bold">Company</th>
                    <th className="px-5 py-4 text-left font-bold">AM Best</th>
                    <th className="px-5 py-4 text-left font-bold">S&amp;P</th>
                    <th className="px-5 py-4 text-left font-bold">Founded</th>
                    <th className="px-5 py-4 text-left font-bold">Best For</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {carriers.map(c => (
                    <tr key={c.name} className="hover:bg-[#F9F7F4]">
                      <td className="px-5 py-4 font-bold text-gray-900">{c.name}</td>
                      <td className="px-5 py-4"><span className={`font-bold ${c.amBest === 'A++' ? 'text-green-600' : 'text-[#D97642]'}`}>{c.amBest}</span></td>
                      <td className="px-5 py-4 text-gray-700">{c.sp}</td>
                      <td className="px-5 py-4 text-gray-700">{c.founded}</td>
                      <td className="px-5 py-4 text-gray-700 text-sm">{c.bestFor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 7 Questions */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">7 Questions to Ask Before Buying Life Insurance in Indiana</h2>
            <div className="space-y-4">
              {[
                { q: '1. What is the carrier\'s AM Best rating?', a: 'Look for A or higher. A++ is the gold standard. Never buy from a carrier rated below A-. Financial strength is the most important factor — this company needs to pay your family decades from now.' },
                { q: '2. What is the carrier\'s claims payment ratio?', a: 'Top carriers pay 99%+ of claims. Check the NAIC complaint ratio — below 1.0 means fewer complaints than average. Ask your agent for the carrier\'s specific claims data.' },
                { q: '3. How does this carrier underwrite my specific health conditions?', a: 'If you have any health conditions (diabetes, high blood pressure, past cancer, etc.), ask specifically how this carrier rates your condition. Different carriers can offer dramatically different rates for the same condition.' },
                { q: '4. Is this an independent agent or a captive agent?', a: 'Captive agents can only sell one company\'s products. Independent agents like Benjamin Wright can shop 20+ carriers to find the best rate for your specific situation. Always work with an independent agent.' },
                { q: '5. What is the contestability period?', a: 'Most policies have a 2-year contestability period during which the insurer can investigate and potentially deny claims. After 2 years, the policy becomes incontestable (with limited exceptions for fraud).' },
                { q: '6. What happens if I miss a premium payment?', a: 'Most policies have a 30-31 day grace period. After that, term policies lapse. Whole life policies may use cash value to pay premiums. Ask specifically about the grace period and reinstatement process.' },
                { q: '7. Can I convert my term policy to permanent coverage?', a: 'Many term policies include a conversion option that lets you convert to whole life without a medical exam. This is valuable if your health changes. Ask about the conversion deadline and which permanent products are available.' },
              ].map(faq => (
                <div key={faq.q} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.q}</h3>
                  <p className="text-gray-700 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Independent vs Captive */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Independent Agent vs. Captive Agent: Why It Matters</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-[#D97642] rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-store-line text-[#D97642] text-2xl" />Independent Agent
                </h3>
                <ul className="space-y-3 text-gray-700 text-sm">
                  {[
                    'Represents 20+ carriers simultaneously',
                    'Shops your application to find the best rate',
                    'Can match your health profile to the most lenient underwriter',
                    'No loyalty to any single company — loyalty to you',
                    'Can switch carriers if a better option emerges',
                    'Same or lower cost than buying direct',
                  ].map(i => <li key={i} className="flex items-start gap-2"><i className="ri-check-line text-[#D97642] mt-1 flex-shrink-0" />{i}</li>)}
                </ul>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-building-line text-gray-600 text-2xl" />Captive Agent
                </h3>
                <ul className="space-y-3 text-gray-700 text-sm">
                  {[
                    'Represents only one company',
                    'Can only offer that company\'s products and rates',
                    'May not be the best fit for your health profile',
                    'Loyalty to their employer, not necessarily to you',
                    'Cannot shop your application to other carriers',
                    'May cost more for the same coverage',
                  ].map(i => <li key={i} className="flex items-start gap-2"><i className="ri-close-line text-red-500 mt-1 flex-shrink-0" />{i}</li>)}
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-4">Let an Independent Agent Shop Indiana's Best Carriers for You</h2>
            <p className="text-xl text-white/80 mb-8">Benjamin Wright represents 20+ top-rated Indiana carriers and will find the best rate for your specific situation — at no extra cost to you.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/instant-quote" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-50 transition-colors whitespace-nowrap">Compare Carrier Rates</Link>
              <Link to="/book-appointment" className="bg-[#0F2A3F] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0A1F2E] transition-colors border-2 border-white/30 whitespace-nowrap">Talk to an Agent</Link>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { to: '/life-insurance-agent-indianapolis', icon: 'ri-user-star-line', title: 'Find an Indianapolis Agent', desc: 'Why working with an independent Indianapolis agent saves you money' },
                { to: '/resources/life-insurance-mistakes-indiana-families', icon: 'ri-error-warning-line', title: '7 Mistakes Indiana Families Make', desc: 'Avoid the most costly life insurance errors Indiana families face' },
                { to: '/resources/term-vs-whole-life-indianapolis-homeowners', icon: 'ri-scales-line', title: 'Term vs. Whole Life Insurance', desc: 'Which policy type is right for Indianapolis homeowners?' },
              ].map(r => (
                <Link key={r.to} to={r.to} className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                  <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className={`${r.icon} text-2xl`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">{r.title}</h3>
                  <p className="text-sm text-gray-600">{r.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Find the Right Carrier for Your Indiana Family?</h2>
            <p className="text-xl text-gray-700 mb-8">Our independent agents shop 20+ top-rated carriers to find the best rate for your specific situation.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3176482983" className="bg-[#1A3A52] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#0F2A3F] transition-colors whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-phone-line mr-2" />Call (317) 648-2983
              </a>
              <Link to="/contact" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors border-2 border-[#1A3A52] whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-message-3-line mr-2" />Send a Message
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
